/**
 * How a Leo conversation is framed on screen. The mode is deliberately independent of the journey
 * that opened Leo, so a conversation started from a widget can be read full page and one started
 * from the top nav can be shrunk down to a card over the page behind it.
 */
export const LEO_PRESENTATION_MODES = {
    FULL_PAGE: 'full',
    DRAWER: 'drawer',
    CARD: 'card',
    MINIMIZED: 'mini',
};

/**
 * Minimized is somewhere to put the card for a moment, not a way to read a conversation, so it is
 * never restored as the user's preferred layout — reopening Leo minimized would look broken.
 */
export const TRANSIENT_LEO_PRESENTATION_MODES = [LEO_PRESENTATION_MODES.MINIMIZED];

/**
 * A full-screen creation flow covers the page, and a right-hand drawer covers the edge Leo docks
 * to, so while either is open only a Leo floating above it stays visible. Docking or going full
 * page would put Leo back behind the drawer with no way out of it.
 */
export const LEO_PRESENTATION_MODES_BLOCKED_BY_COVERING_DRAWER = [
    LEO_PRESENTATION_MODES.FULL_PAGE,
    LEO_PRESENTATION_MODES.DRAWER,
];

export const DEFAULT_LEO_PRESENTATION_MODE = LEO_PRESENTATION_MODES.DRAWER;

/**
 * @param {unknown} mode
 * @returns {boolean}
 */
export function isAvailableLeoPresentationMode (mode) {
    return Object.values(LEO_PRESENTATION_MODES).includes(mode);
}

/**
 * The layout is held in the store rather than the URL. Both params are still read once on load, and
 * stripped whenever Leo's params are, so links shared while they were written still work.
 */
export const LEO_PRESENTATION_MODE_PARAM = 'leoMode';

export const LEGACY_LEO_FULL_PAGE_PARAM = 'expanded';

/**
 * Held in sessionStorage rather than localStorage: a layout is a choice about the work in front of
 * you, not a lasting preference, so each new session starts Leo in the side drawer and the user
 * takes it from there. Coming back days later to a full page Leo, or a card left somewhere over
 * the page, asks the user to undo a decision they no longer remember making.
 */
export const LEO_PRESENTATION_MODE_STORAGE_KEY = 'pendo:leoPresentationMode';
